/* eslint-disable operator-linebreak */
import React, { useEffect, useMemo, useState } from 'react';
import { NSNavTabs } from 'bricks';
import { TabContent } from 'reactstrap';
import RenderIf from 'components/RenderIf/RenderIf';
import ReadOnlyWrapper from 'components/ReadOnlyWrapper/ReadOnlyWrapper';
import TopLevelAnalysis from './TopLevelAnalysis/TopLevelAnalysis';
import ProFormaGenericTable from '../../ProFormaGenericTable/ProFormaGenericTable';
import {
    WATERFALL_SUMMARY_COLUMNS,
    WATERFALL_TABS,
    GP_EXPANDED,
    LP_EXPANDED,
    OVERVIEW,
    transformContributionsRows,
    transformDistributionWaterfallRows,
    transformDistributionRowsCashFlow,
    transformNetCashFlowsRows,
    transformWaterfallSummaryRows,
    transformGpContributionsRows,
    transformGpDistributionWaterfallRows,
    transformGpDistributionRowsCashFlow,
    transformGpNetCashFlowsRows,
} from './constants';
import './WaterfallAnalysis.scss';
import ContributionsAndDistributions from './ContributionsAndDistributions/ContributionsAndDistributions';
import { useProFormaSocketContext } from '../../ProForma/socketContext/ProFormaSocketProvider';
import { getContributionsTotals } from './ContributionsAndDistributions/constants';

export interface IWaterfallAnalysisProps {
    hasWriteAccessToProForma: boolean;
}

const WaterfallAnalysis = ({ hasWriteAccessToProForma }: IWaterfallAnalysisProps) => {
    const {
        waterfallAnalysis,
        handleUpdateDistributionWaterfall,
        handleUpdateProFormaPartners,
        proForma,
        cashFlowPeriodsByCadence,
        periodCadence,
    } = useProFormaSocketContext();
    const [activeTabName, setActiveTabName] = useState<string>(OVERVIEW);
    const [tabsDisplay, setTabsDisplay] = useState({ isSingleLp: true, isSingleGp: true });

    useEffect(() => {
        if (waterfallAnalysis && waterfallAnalysis.isSingleLp !== undefined && waterfallAnalysis.isSingleGp !== undefined) {
            setTabsDisplay({ isSingleLp: waterfallAnalysis?.isSingleLp, isSingleGp: waterfallAnalysis?.isSingleGp });
        }
    }, [waterfallAnalysis]);

    const waterfallType = !waterfallAnalysis.isSingleGp && activeTabName === GP_EXPANDED ? 'multi_gp' : 'basic';

    const contributionsRowsPopulated = React.useMemo(
        () => transformContributionsRows(
            waterfallAnalysis?.gp,
            waterfallAnalysis?.lp,
            waterfallAnalysis?.contributions,
            waterfallAnalysis?.contributionsCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
        ),
        [
            waterfallAnalysis?.gp,
            waterfallAnalysis?.lp,
            waterfallAnalysis?.contributions,
            waterfallAnalysis?.contributionsCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
        ],
    );

    const gpContributionsRowsPopulated = React.useMemo(
        () => transformGpContributionsRows(
            waterfallAnalysis?.gp,
            waterfallAnalysis?.gpGpContributions,
            waterfallAnalysis?.gpGpContributionsCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
        ),
        [
            waterfallAnalysis?.gp,
            waterfallAnalysis?.gpGpContributions,
            waterfallAnalysis?.gpGpContributionsCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
        ],
    );

    const distributionWaterfallRowsPopulated = React.useMemo(
        () => transformDistributionWaterfallRows(
            waterfallAnalysis?.distributionWaterfall,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.distributionWaterfallCashFlow,
        ),
        [
            waterfallAnalysis?.distributionWaterfall,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.distributionWaterfallCashFlow,
        ],
    );

    const gpDistributionWaterfallRowsPopulated = React.useMemo(
        () => transformGpDistributionWaterfallRows(
            waterfallAnalysis?.gpGpDistributionWaterfall,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.gpGpDistributionWaterfallCashFlow,
        ),
        [
            waterfallAnalysis?.gpGpDistributionWaterfall,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.gpGpDistributionWaterfallCashFlow,
        ],
    );

    const distributionsRowsPopulated = React.useMemo(
        () => transformDistributionRowsCashFlow(
            waterfallAnalysis?.distributions,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.distributionsCashFlow,
        ),
        [
            waterfallAnalysis?.distributions,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.distributionsCashFlow,
        ],
    );

    const gpDistributionRowsPopulated = React.useMemo(
        () => transformGpDistributionRowsCashFlow(
            waterfallAnalysis?.gpGpDistributions,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.gpGpDistributionsCashFlow,
        ),
        [
            waterfallAnalysis?.gpGpDistributions,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.gpGpDistributionsCashFlow,
        ],
    );

    const netCashFlowRowsPopulated = React.useMemo(
        () => transformNetCashFlowsRows(
            waterfallAnalysis?.netCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.netCashFlowCashFlow,
        ),
        [
            waterfallAnalysis?.netCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.netCashFlowCashFlow,
        ],
    );

    const gpNetCashFlowRowsPopulated = React.useMemo(
        () => transformGpNetCashFlowsRows(
            waterfallAnalysis?.gpGpNetCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.gpGpNetCashFlowCashFlow,
        ),
        [
            waterfallAnalysis?.gpGpNetCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.gpGpNetCashFlowCashFlow,
        ],
    );

    const waterfallSummaryRows = React.useMemo(
        () => transformWaterfallSummaryRows(tabsDisplay.isSingleGp, waterfallAnalysis?.waterfallSummary),
        [waterfallAnalysis?.waterfallSummary, tabsDisplay],
    );

    const handleTabsDisplayChange = (key: string, value: boolean) => {
        setTabsDisplay(prev => ({ ...prev, [key]: value }));
    };

    const tabs = WATERFALL_TABS.filter(tab => (
        (!tabsDisplay.isSingleLp && tab === LP_EXPANDED) ||
        (!tabsDisplay.isSingleGp && tab === GP_EXPANDED) ||
        ((!tabsDisplay.isSingleLp || !tabsDisplay.isSingleGp) && tab === OVERVIEW)
    ));

    const updateDistributionWaterfallRow = (rowId: number, dataKey: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        const updateMap: Record<number, Record<string, Record<string, number>>> = {
            0: {
                upTo: { preferredReturnHurdleRate: value },
            },
            1: {
                upTo: { secondTierHurdleRate: value },
                gpPromote: { secondTierGpPromote: value },
            },
            2: {
                upTo: { thirdTierHurdleRate: value },
                gpPromote: { thirdTierGpPromote: value },
            },
            3: {
                gpPromote: { fourthTierGpPromote: value },
            },
        };

        const updateData = updateMap[rowId]?.[dataKey];
        if (updateData) {
            handleUpdateDistributionWaterfall(waterfallType, updateData);
        }
    };

    const updateContributionRow = (rowId: number, dataKey: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = dataKey === 'share'
            ? (parseFloat(e.target.value)).toString()
            : e.target.value;

        const updateData = {
            id: rowId,
            [dataKey === 'share' ? 'contributionShare' : 'name']: value,
        };

        handleUpdateProFormaPartners(updateData);
    };

    const contributionsTotalsWithPeriods = useMemo(() => {
        const contributionsTotals = activeTabName === OVERVIEW ? {
            amount: waterfallAnalysis?.contributions?.totalAmount,
            share: waterfallAnalysis?.contributions?.totalSharePercent,
        } : {
            amount: waterfallAnalysis?.gpGpContributions?.totalEquityAmount,
            share: waterfallAnalysis?.gpGpContributions?.totalShareOfDevFee,
        };

        const totals = getContributionsTotals(
            activeTabName === OVERVIEW ? waterfallAnalysis.contributionsCashFlow : waterfallAnalysis.gpGpContributionsCashFlow,
            cashFlowPeriodsByCadence,
            periodCadence,
        );
        return {
            ...contributionsTotals,
            ...totals,
        };
    }, [waterfallAnalysis?.contributionsCashFlow, cashFlowPeriodsByCadence, periodCadence, activeTabName]);

    const distributionTotalsWithPeriods = useMemo(() => {
        const distributionTotals = activeTabName === OVERVIEW ? {
            amount: waterfallAnalysis?.distributions?.totalAmount,
            share: waterfallAnalysis?.distributions?.totalShare,
        } : {
            amount: waterfallAnalysis?.gpGpDistributions?.totalAmount,
            share: waterfallAnalysis?.gpGpDistributions?.totalShare,
        };

        const totals = getContributionsTotals(
            activeTabName === OVERVIEW ? waterfallAnalysis.distributionsCashFlow : waterfallAnalysis.gpGpDistributionsCashFlow,
            cashFlowPeriodsByCadence,
            periodCadence,
        );
        return {
            ...distributionTotals,
            ...totals,
        };
    }, [waterfallAnalysis?.distributionsCashFlow, cashFlowPeriodsByCadence, periodCadence, activeTabName]);

    const netCashFlowTotalsWithPeriods = useMemo(() => {
        const netCashFlowTotals = activeTabName === OVERVIEW ? {
            amount: waterfallAnalysis?.netCashFlow?.totalAmount,
            share: waterfallAnalysis?.netCashFlow?.totalShare,
            emx: waterfallAnalysis?.netCashFlow?.totalEmx,
            irr: waterfallAnalysis?.netCashFlow?.totalIrr,
        } : {
            amount: waterfallAnalysis?.gpGpNetCashFlow?.totalAmount,
            share: waterfallAnalysis?.gpGpNetCashFlow?.totalShare,
            emx: waterfallAnalysis?.gpGpNetCashFlow?.totalEmx,
            irr: waterfallAnalysis?.gpGpNetCashFlow?.totalIrr,
        };

        const totals = getContributionsTotals(
            activeTabName === OVERVIEW ? waterfallAnalysis.netCashFlowCashFlow : waterfallAnalysis.gpGpNetCashFlowCashFlow,
            cashFlowPeriodsByCadence,
            periodCadence,
        );
        return {
            ...netCashFlowTotals,
            ...totals,
        };
    }, [waterfallAnalysis?.netCashFlow, cashFlowPeriodsByCadence, periodCadence, activeTabName]);

    const BasicTables = (
        <ContributionsAndDistributions
            waterfallType={waterfallType}
            contributionsRows={contributionsRowsPopulated}
            distributionWaterfallRows={distributionWaterfallRowsPopulated}
            updateDistributionWaterfallRow={updateDistributionWaterfallRow}
            updateContributionRow={updateContributionRow}
            distributionsRows={distributionsRowsPopulated}
            netCashFlowRows={netCashFlowRowsPopulated}
            contributionsTotals={contributionsTotalsWithPeriods}
            distributionsTotals={distributionTotalsWithPeriods}
            netCashFlowTotals={netCashFlowTotalsWithPeriods}
        />
    );

    return (
        <ReadOnlyWrapper isReadOnly={!hasWriteAccessToProForma}>
            <div className="WaterfallAnalysis">
                <TopLevelAnalysis onChange={handleTabsDisplayChange} />
                <div className="WaterfallAnalysis__separator mx-3 mb-3" />
                <div className="w-100 px-3 pb-3">
                    <ProFormaGenericTable
                        fixedColumnsMaxWidth="100%"
                        columns={WATERFALL_SUMMARY_COLUMNS}
                        rows={waterfallSummaryRows}
                        setRows={() => { }}
                        hideAddButton
                        hideTotalFooter
                        hideCashFlowTotals
                    />
                </div>
                <div className="WaterfallAnalysis__separator mx-3 mb-3" />
                <div className="w-100 px-3 pb-3">
                    <RenderIf isTrue={tabsDisplay.isSingleGp && tabsDisplay.isSingleLp}>
                        {BasicTables}
                    </RenderIf>
                    <RenderIf isTrue={!tabsDisplay.isSingleLp || !tabsDisplay.isSingleGp}>
                        <NSNavTabs
                            containerClassName="d-flex mt-2"
                            navItems={tabs.map((navName, index) => ({
                                callback: () => setActiveTabName(navName),
                                name: navName,
                                isActive: activeTabName === navName,
                                id: `watefall-analysis-${index}`,
                                className: 'WaterfallAnalysis__tabs text-dark text-nowrap px-4 mr-2',
                            }))}
                        />

                        <TabContent activeTab={activeTabName} className="NSCard--level-3 d-flex w-100 h-100 pl-3 pt-3 pb-3">
                            <RenderIf isTrue={activeTabName === OVERVIEW}>
                                {BasicTables}
                            </RenderIf>
                            <RenderIf isTrue={activeTabName === GP_EXPANDED}>
                                <ContributionsAndDistributions
                                    waterfallType={waterfallType}
                                    contributionsRows={gpContributionsRowsPopulated}
                                    distributionWaterfallRows={gpDistributionWaterfallRowsPopulated}
                                    updateDistributionWaterfallRow={updateDistributionWaterfallRow}
                                    updateContributionRow={updateContributionRow}
                                    distributionsRows={gpDistributionRowsPopulated}
                                    netCashFlowRows={gpNetCashFlowRowsPopulated}
                                    distributionsTotals={distributionTotalsWithPeriods}
                                    netCashFlowTotals={netCashFlowTotalsWithPeriods}
                                    contributionsTotals={contributionsTotalsWithPeriods}
                                />
                            </RenderIf>
                        </TabContent>
                    </RenderIf>
                </div>
            </div>
        </ReadOnlyWrapper>
    );
};

export default WaterfallAnalysis;
